/**
 * ViewDataContext - Centralized context and hook exports for ViewDetailV2.
 *
 * This file consolidates all context-related exports in one place,
 * eliminating the need for nested index.ts files.
 */
import { createContext, ReactNode, useContext, useEffect, useRef } from 'react'

import type { ElementSaveHandler } from '@/components/ElementDetailV2'
import { StructureObjectDto } from '@/endpoints/models'
import { useRegisterSaveBeforeLeave } from '@/pages/User/pages/Home/context/SaveBeforeLeaveContext'
import {
	ObjectSettings,
	SourceItem,
	Status,
	View,
	ViewColumn,
	ViewPermission,
	ViewSourcesData,
} from '@/rtkApi/mddApiSliceGeneratedV2'

// Re-export the hook and types from the implementation
export {
	useViewDataV2,
	type UseViewDataV2Return,
	type ViewFormData,
	ViewTabV2,
} from './hooks/useViewDataV2'

import { useViewDataV2, ViewTabV2 } from './hooks/useViewDataV2'

// Context value type - matches useViewDataV2 return type
interface ViewDataContextValue {
	/** Bumps on each local edit; restarts debounced autosave in ElementDetailV2 */
	autosaveResetKey: number,
	columns: ViewColumn[],
	dirty: boolean,
	editMode: boolean,
	form: {
		businessDomainId?: number,
		businessDomainName?: string,
		code?: string,
		columns: ViewColumn[],
		comment?: string,
		criteriaDescription?: string,
		criteriaScript?: string,
		customAttributes?: unknown[],
		description?: string,
		name?: string,
		objectPermissions: ViewPermission[],
		objectSettings?: ObjectSettings[]
		ownerId?: number,
		ownerName?: string,
		responsibleAnalystId?: number,
		responsibleAnalystName?: string,
		responsiblePersonId?: number,
		responsiblePersonName?: string,
		sourceDescription?: string,
		sourceItems: SourceItem[],
		sourceScript?: string,
		status?: Status,
		stereotypeId?: number,
		stereotypeName?: string
	} | null,

	/** Bumps when view data is reloaded from server (AI sync, cancel). */
	serverDataRevision: number,
	isLoading: boolean,
	isSaving: boolean,
	node: StructureObjectDto,
	objectTypes: ObjectSettings[] | undefined,

	onCancel: (node: StructureObjectDto) => void,
	onChange: (update: Record<string, unknown>) => void,
	onColumnsChange: (columns: ViewColumn[]) => void,

	onEdit: () => void,
	onPermissionsChange: (permissions: ViewPermission[]) => void,
	onSave: ElementSaveHandler,
	onSourceItemsChange: (sourceItems: SourceItem[]) => void,

	onSourcesDataChange: (update: Partial<ViewSourcesData>) => void,
	onTabChange: (tab: ViewTabV2) => void,
	permissions: ViewPermission[],
	selectedTab: ViewTabV2,
	sourceItems: SourceItem[],

	sourcesData: ViewSourcesData | undefined,
	systemNodeId: number,
	view: View | undefined
}

const ViewDataContext = createContext<ViewDataContextValue | null>(null)

export const ViewDataProvider = ({ children }: { children: ReactNode }) => {
	const viewData = useViewDataV2()
	const registerSave = useRegisterSaveBeforeLeave()
	const saveRef = useRef<() => Promise<void>>(async () => {})

	saveRef.current = async () => {
		if (viewData.editMode && viewData.dirty) {
			await viewData.onSave({ skipRefetch: true })
		}
	}

	useEffect(() => {
		return registerSave(() => saveRef.current())
	}, [registerSave])

	return (
		<ViewDataContext.Provider value={viewData as unknown as ViewDataContextValue}>
			{children}
		</ViewDataContext.Provider>
	)
}

export const useViewDataContext = (): ViewDataContextValue => {
	const context = useContext(ViewDataContext)
	if (!context) {
		throw new Error('useViewDataContext must be used within a ViewDataProvider')
	}
	return context
}
